import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSave } from '@fortawesome/free-solid-svg-icons';
import { Input, SaveButton, Checkbox } from './styled';

const ColumnManagement = ({ tableName }) => {
  const [boardColumns, setBoardColumns] = useState([]);
  const [newColumns, setNewColumns] = useState([]);

  // 컬럼 데이터 조회
  useEffect(() => {
    const fetchBoardColumns = async () => {
      try {
        const response = await fetch(`/request/getColumnsInfo?boardId=${tableName}`, {
          credentials: 'include'
        });
        const data = await response.json();
        console.log('Fetched columns data:', data);
        
        if (Array.isArray(data)) {
          setBoardColumns(data);
        } else {
          setBoardColumns([]);
        }
      } catch (error) {
        console.error('Error fetching board columns:', error);
        setBoardColumns([]);
      }
    };

    if (tableName) {
      fetchBoardColumns();
    }
  }, [tableName]);

  // 컬럼 추가 함수 (더 상세한 정보 포함)
  const addNewColumn = () => {
    setNewColumns([
      ...newColumns,
      {
        columnId: "",
        columnName: "",
        dataType: "VARCHAR",          // DB 데이터 타입
        columnSize: "255",            // 컬럼 크기
        isNullable: true,             // NULL 허용 여부
        isPrimaryKey: false,          // PK 여부
        isForeignKey: false,          // FK 여부
        foreignKeyTable: "",          // FK 참조 테이블
        foreignKeyColumn: "",         // FK 참조 컬럼
        defaultValue: "",             // 기본값
        controlType: "TextBox",       // UI 컨트롤 타입
        isAutoIncrement: false,       // AUTO INCREMENT 여부
      },
    ]);
  };

  // 컬럼 변경 핸들러
  const handleColumnChange = (e, index, field) => {
    const { value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    const updatedColumns = [...newColumns];
    updatedColumns[index] = { ...updatedColumns[index], [field]: newValue };
    
    // PK 선택시 자동으로 NOT NULL 설정
    if (field === 'isPrimaryKey' && checked) {
      updatedColumns[index].isNullable = false;
    }
    
    setNewColumns(updatedColumns);
  };

  // 컬럼 저장 (실제 DB 스키마 변경)
  const handleSaveColumns = async () => {
    try {
      const response = await fetch("/request/addDatabaseColumns", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tableName: tableName,
          columns: newColumns
        }),
      });

      if (response.ok) {
        alert('컬럼이 성공적으로 추가되었습니다!');
        setNewColumns([]);
        // 컬럼 목록 새로고침
        window.location.reload();
      } else {
        const error = await response.text();
        alert(`컬럼 추가 실패: ${error}`);
      }
    } catch (error) {
      console.error("Error saving columns:", error);
      alert('컬럼 저장 중 오류가 발생했습니다.');
    }
  };

  // 컬럼 삭제 (실제 DB에서 DROP COLUMN)
  const handleRemoveColumn = async (columnId, isExisting) => {
    if (isExisting) {
      if (window.confirm(`정말로 '${columnId}' 컬럼을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
        try {
          const response = await fetch("/request/dropDatabaseColumn", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              tableName: tableName,
              columnName: columnId,
            }),
          });

          if (response.ok) {
            alert('컬럼이 성공적으로 삭제되었습니다!');
            setBoardColumns(boardColumns.filter(col => (col.column_id || col.columnId) !== columnId));
          } else {
            const error = await response.text();
            alert(`컬럼 삭제 실패: ${error}`);
          }
        } catch (error) {
          console.error("Error deleting column:", error);
          alert('컬럼 삭제 중 오류가 발생했습니다.');
        }
      }
    } else {
      // 신규 컬럼에서 제거
      const index = newColumns.findIndex(col => col === columnId);
      const updatedNewColumns = [...newColumns];
      updatedNewColumns.splice(index, 1);
      setNewColumns(updatedNewColumns);
    }
  };

  return (
    <div style={{ padding: '20px', backgroundColor: 'white' }}>
      {/* 기존 컬럼 섹션 */}
      <fieldset style={{
        border: '2px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        backgroundColor: '#f8f9fa'
      }}>
        <legend style={{
          padding: '0 8px',
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333'
        }}>
          📋 현재 테이블 컬럼 ({tableName})
        </legend>
        
        {boardColumns.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6c757d', fontStyle: 'italic' }}>
            등록된 컬럼이 없습니다.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#e9ecef' }}>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>컬럼명</th>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>표시명</th>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>데이터타입</th>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>크기</th>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>NULL</th>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>PK</th>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>FK</th>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>기본값</th>
                  <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>액션</th>
                </tr>
              </thead>
              <tbody>
                {boardColumns.map((column, index) => (
                  <tr key={index}>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <strong>{column.column_id || column.columnId}</strong>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      {column.column_title || column.columnName || '-'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      {column.data_type || column.dataType || '-'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      {column.character_maximum_length || column.columnSize || '-'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      {column.is_nullable || column.isNullable ? 'YES' : 'NO'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      {column.is_primary_key || column.isPrimaryKey ? 'PK' : '-'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      {column.is_foreign_key || column.isForeignKey ? 'FK' : '-'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      {column.column_default || column.defaultValue || '-'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                      <SaveButton 
                        onClick={() => handleRemoveColumn(column.column_id || column.columnId, true)}
                        style={{ 
                          backgroundColor: '#dc3545',
                          padding: '4px 8px',
                          fontSize: '11px',
                          width: 'auto',
                          margin: 0
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </SaveButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </fieldset>

      {/* 신규 컬럼 추가 섹션 */}
      <fieldset style={{
        border: '2px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        backgroundColor: '#f8f9fa'
      }}>
        <legend style={{
          padding: '0 8px',
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333'
        }}>
          ➕ 새 컬럼 추가 (실제 DB 스키마 변경)
        </legend>

        {newColumns.map((column, index) => (
          <div key={index} style={{
            padding: '16px',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            marginBottom: '12px'
          }}>
            {/* 기본 정보 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                  컬럼명 (영문):
                </label>
                <Input
                  type="text"
                  placeholder="column_name"
                  value={column.columnId || ""}
                  onChange={(e) => handleColumnChange(e, index, "columnId")}
                  style={{ margin: 0, fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                  표시명 (한글):
                </label>
                <Input
                  type="text"
                  placeholder="컬럼 표시명"
                  value={column.columnName || ""}
                  onChange={(e) => handleColumnChange(e, index, "columnName")}
                  style={{ margin: 0, fontSize: '13px' }}
                />
              </div>
            </div>

            {/* 데이터 타입 및 크기 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                  데이터 타입:
                </label>
                <select
                  value={column.dataType || ""}
                  onChange={(e) => handleColumnChange(e, index, "dataType")}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <option value="VARCHAR">VARCHAR</option>
                  <option value="TEXT">TEXT</option>
                  <option value="INTEGER">INTEGER</option>
                  <option value="BIGINT">BIGINT</option>
                  <option value="DECIMAL">DECIMAL</option>
                  <option value="BOOLEAN">BOOLEAN</option>
                  <option value="DATE">DATE</option>
                  <option value="TIMESTAMP">TIMESTAMP</option>
                  <option value="JSON">JSON</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                  크기:
                </label>
                <Input
                  type="text"
                  placeholder="255"
                  value={column.columnSize || ""}
                  onChange={(e) => handleColumnChange(e, index, "columnSize")}
                  style={{ margin: 0, fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                  기본값:
                </label>
                <Input
                  type="text"
                  placeholder="기본값 (선택)"
                  value={column.defaultValue || ""}
                  onChange={(e) => handleColumnChange(e, index, "defaultValue")}
                  style={{ margin: 0, fontSize: '13px' }}
                />
              </div>
            </div>

            {/* 제약조건 */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                <Checkbox
                  type="checkbox"
                  checked={!column.isNullable}
                  onChange={(e) => handleColumnChange(e, index, "isNullable")}
                  style={{ marginRight: '6px' }}
                />
                NOT NULL
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                <Checkbox
                  type="checkbox"
                  checked={column.isPrimaryKey}
                  onChange={(e) => handleColumnChange(e, index, "isPrimaryKey")}
                  style={{ marginRight: '6px' }}
                />
                Primary Key
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                <Checkbox
                  type="checkbox"
                  checked={column.isAutoIncrement}
                  onChange={(e) => handleColumnChange(e, index, "isAutoIncrement")}
                  style={{ marginRight: '6px' }}
                />
                AUTO INCREMENT
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                <Checkbox
                  type="checkbox"
                  checked={column.isForeignKey}
                  onChange={(e) => handleColumnChange(e, index, "isForeignKey")}
                  style={{ marginRight: '6px' }}
                />
                Foreign Key
              </label>
            </div>

            {/* FK 설정 (FK 체크시에만 보임) */}
            {column.isForeignKey && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px', backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                    참조 테이블:
                  </label>
                  <Input
                    type="text"
                    placeholder="참조할 테이블명"
                    value={column.foreignKeyTable || ""}
                    onChange={(e) => handleColumnChange(e, index, "foreignKeyTable")}
                    style={{ margin: 0, fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                    참조 컬럼:
                  </label>
                  <Input
                    type="text"
                    placeholder="참조할 컬럼명"
                    value={column.foreignKeyColumn || ""}
                    onChange={(e) => handleColumnChange(e, index, "foreignKeyColumn")}
                    style={{ margin: 0, fontSize: '13px' }}
                  />
                </div>
              </div>
            )}

            {/* UI 컨트롤 타입 */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                UI 컨트롤 타입:
              </label>
              <select
                value={column.controlType || ""}
                onChange={(e) => handleColumnChange(e, index, "controlType")}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              >
                <option value="TextBox">텍스트박스</option>
                <option value="NumberBox">숫자박스</option>
                <option value="SelectBox">선택상자</option>
                <option value="CheckBox">체크박스</option>
                <option value="DateTimeBox">날짜선택</option>
                <option value="TextArea">텍스트영역</option>
                <option value="FileUpload">파일업로드</option>
              </select>
            </div>

            <div style={{ textAlign: 'right' }}>
              <SaveButton 
                onClick={() => handleRemoveColumn(column, false)}
                style={{ 
                  backgroundColor: '#6c757d',
                  padding: '6px 12px',
                  fontSize: '12px',
                  width: 'auto',
                  margin: 0
                }}
              >
                <FontAwesomeIcon icon={faTrash} /> 제거
              </SaveButton>
            </div>
          </div>
        ))}

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <SaveButton 
            onClick={addNewColumn}
            style={{ 
              backgroundColor: '#28a745',
              padding: '10px 20px',
              width: 'auto',
              margin: '0 8px'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> 컬럼 추가
          </SaveButton>

          {newColumns.length > 0 && (
            <SaveButton 
              onClick={handleSaveColumns}
              style={{ 
                backgroundColor: '#007bff',
                padding: '12px 24px',
                width: 'auto',
                margin: '0 8px'
              }}
            >
              <FontAwesomeIcon icon={faSave} /> 모든 컬럼 저장
            </SaveButton>
          )}
        </div>
      </fieldset>

      <div style={{ 
        backgroundColor: '#fff3cd', 
        border: '1px solid #ffeaa7', 
        borderRadius: '4px', 
        padding: '12px', 
        fontSize: '12px',
        color: '#856404'
      }}>
        ⚠️ <strong>주의:</strong> 컬럼 추가/삭제는 실제 데이터베이스 구조를 변경합니다. 
        운영 환경에서는 신중하게 진행하세요.
      </div>
    </div>
  );
};

export default ColumnManagement;